import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { ReportView } from '../../../components/ui';
import { fetcher } from '../../../services/fetcher';
import { normalizePaginatedListResponse } from '../../../services/paginatedList';
import { RulesReport, type WrhRulesReportSet } from './RulesReport';
import { useManagementCompanyLogoBlobUrl } from './useManagementCompanyLogo';

type ManagementCompanyRow = {
  id: number;
  name: string;
  isActive?: boolean;
  logoStorageKey?: string | null;
  sortOrder?: number;
};

const RulesReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [params] = useSearchParams();
  const ruleSetIdParam = params.get('id')?.trim() || '';
  const ruleSetId = ruleSetIdParam ? Number(ruleSetIdParam) : null;

  const { data: ruleSets, error, isLoading } = useSWR<WrhRulesReportSet[]>('/wrh/rules', fetcher, {
    revalidateOnFocus: false,
  });
  const { data: companiesRaw } = useSWR('/management-companies?page=1&limit=50', fetcher, {
    revalidateOnFocus: false,
  });

  const managementCompany = useMemo(() => {
    const list = normalizePaginatedListResponse<ManagementCompanyRow>(companiesRaw, 1, 50).data;
    const active = list.filter((c) => c.isActive !== false);
    const withLogo = active.find((c) => c.logoStorageKey) ?? list.find((c) => c.logoStorageKey);
    return withLogo
      ? { id: withLogo.id, name: withLogo.name, logoStorageKey: withLogo.logoStorageKey ?? null }
      : null;
  }, [companiesRaw]);

  const { logoBlobUrl: companyLogoSrc } = useManagementCompanyLogoBlobUrl(
    managementCompany?.id,
    managementCompany?.logoStorageKey,
  );

  const ruleSet = useMemo(() => {
    if (!ruleSets?.length) return null;
    if (ruleSetId != null && Number.isFinite(ruleSetId)) {
      return ruleSets.find((s) => s.id === ruleSetId) ?? null;
    }
    return ruleSets.find((s) => s.isActive) ?? ruleSets[0];
  }, [ruleSets, ruleSetId]);

  const backHref = '/wrh/rules';

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  if (error || !ruleSet) {
    return (
      <div className={cn('flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4', colors.background)}>
        <p className={cn('text-sm', colors.textMuted)}>Could not load WRH rule set.</p>
        <Link to={backHref} className={cn('text-sm font-medium', colors.primaryFg, 'hover:underline')}>
          ← Back to rules
        </Link>
      </div>
    );
  }

  return (
    <ReportView
      backTo={backHref}
      backLabel="Back to rules"
      title="WRH rules"
      subtitle={`${ruleSet.name}${ruleSet.isActive ? ' · Default' : ''}`}
      printPageSize="A4 portrait"
      printMargin="10mm"
    >
      <RulesReport
        data={{ ruleSet, managementCompany }}
        companyLogoSrc={companyLogoSrc}
      />
    </ReportView>
  );
};

export default RulesReportPage;
